import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, TextField, Typography } from "@mui/material";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../helpers/axios";
import { fetchEvents } from "../../store/slices/globalSlice";
import { ConfirmationAlert, errorAlert, successAlert } from "../../utils/services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import { EVENT_COLOR, eventsForDate } from "../../utils/companyEvents";

export default function AddEvents() {
  const dispatch = useDispatch();
  const { events: allEvents, eventsLoading, eventsError } = useSelector((state) => state.global);
  const events = allEvents.filter((event) => event.endDate > format(new Date(), "yyyy-MM-dd"));
  const [name, setName] = useState("");
  const [range, setRange] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { dispatch(fetchEvents()); }, [dispatch]);

  const startDate = range?.[0] ? format(range[0], "yyyy-MM-dd") : "";
  const endDate = range?.[1] ? format(range[1], "yyyy-MM-dd") : "";
  const reset = () => { setName(""); setRange(null); setEditId(null); };
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim() || !startDate || !endDate) {
      errorAlert("Enter an event name and select both dates on the calendar.");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), startDate, endDate };
      if (editId) await axiosInstance.put(`/company-events/${editId}`, payload, { memberRequest: true });
      else await axiosInstance.post("/company-events", payload, { memberRequest: true });
      successAlert(editId ? "Event updated for all firm calendars." : "Event added to all firm calendars.");
      reset();
      dispatch(fetchEvents());
    } catch (error) {
      errorAlert(error.response?.data?.error || "Unable to save the event. Please try again.");
    } finally { setSaving(false); }
  };
  const confirmRemove = (event) => {
    const toastId = `delete-firm-event-${event._id}`;
    toast(
      <ConfirmationAlert
        question={`Are you sure to delete "${event.name}" from all firm calendars?`}
        answer="Yes, Delete it"
        icon={<RiDeleteBin6Line />}
        onDeleteOff={() => {
          toast.dismiss(toastId);
          remove(event);
        }}
      />,
      {
        toastId,
        position: toast.POSITION.TOP_CENTER,
        closeButton: true,
        autoClose: false,
        draggable: false,
      }
    );
  };
  const remove = async (event) => {
    setSaving(true);
    try {
      await axiosInstance.delete(`/company-events/${event._id}`, { memberRequest: true });
      if (editId === event._id) reset();
      dispatch(fetchEvents());
      successAlert("Event removed.");
    } catch (error) {
      errorAlert(error.response?.data?.error || "Unable to remove the event.");
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <ToastContainer />
      <CardContent>
        <Typography variant="h5" gutterBottom>Firm events</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select the first and last day, then enter an event name. Every day in the range appears on everyone's calendar. Time and expense entries remain available.
        </Typography>
        <Box component="form" onSubmit={save} sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ pointerEvents: saving ? "none" : "auto" }}>
            <Calendar
              key={editId || "new-event"}
              selectRange
              allowPartialRange
              value={range}
              onChange={setRange}
              tileContent={({ date, view }) => {
                if (view !== "month") return null;
                const dayEvents = eventsForDate(events, format(date, "yyyy-MM-dd"));
                return (
                  <Box sx={{ height: 12, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {dayEvents.length > 0 && (
                      <Box
                        component="span"
                        role="img"
                        aria-label={`Firm event: ${dayEvents.map((event) => event.name).join(", ")}`}
                        title={dayEvents.map((event) => event.name).join(", ")}
                        sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: EVENT_COLOR }}
                      />
                    )}
                  </Box>
                );
              }}
            />
            <Typography variant="body2" sx={{ mt: 1 }} aria-live="polite">
              {startDate ? `${startDate} — ${endDate || "Select the last day"}` : "Select an event date range"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <TextField label="Event name" placeholder="Going to Singapore" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} disabled={saving} required fullWidth />
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button type="submit" variant="contained" disabled={saving} sx={{ backgroundColor: EVENT_COLOR }}>
                {saving ? "Saving…" : editId ? "Save event" : "Add event"}
              </Button>
              <Button type="button" onClick={reset} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Box>
        {eventsError && <Typography role="alert" color="error" sx={{ mt: 2 }}>Unable to load events. <Button onClick={() => dispatch(fetchEvents())}>Retry</Button></Typography>}
        {eventsLoading && <Typography sx={{ mt: 2 }}>Loading events…</Typography>}
        {!eventsLoading && !eventsError && events.length === 0 && <Typography sx={{ mt: 2 }}>No firm events yet.</Typography>}
        {events.map((event) => (
          <Box key={event._id} sx={{ mt: 2, p: 2, borderLeft: `4px solid ${EVENT_COLOR}`, backgroundColor: "#f8f5f2", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600}>{event.name}</Typography>
              <Typography variant="body2">{event.startDate} — {event.endDate}</Typography>
            </Box>
            <Button disabled={saving} onClick={() => {
              setEditId(event._id); setName(event.name);
              setRange([new Date(`${event.startDate}T12:00:00`), new Date(`${event.endDate}T12:00:00`)]);
            }}>Edit</Button>
            <Button color="error" disabled={saving} onClick={() => confirmRemove(event)}>Remove</Button>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
